import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { find, isEmpty } from "lodash";

import { Typography } from "@/components/Typography";
import { StarsRating } from "@/components/StarsRating";
import { FeedbackHeader } from "./FeedbackHeader";
import { FeedbackNavigation } from "./FeedbackNavigation";

import { FEEDBACK_PREVIEW_INDEX } from "@/utils/feedbackPreviewNavigation";

import { QRFeedbackContextProps } from "@/contexts/QRFeedbackContext/types";
import { QRFeedbackContext } from "@/contexts/QRFeedbackContext";

import { SingleFeedbackProps } from "./types";

const MAX_CHARACTERS = 1000;

export const SingleFeedback = ({
  data,
  isInitPreview,
  hasMultipleCategories,
  category,
}: SingleFeedbackProps) => {
  const { themeColor } = data;
  const { t } = useTranslation("feedback");

  const [count, setCount] = useState(0);
  const [reviewComment, setReviewComment] = useState(undefined);

  const qrFeedbackContextData = useContext<QRFeedbackContextProps>(QRFeedbackContext);

  const {
    selectedReviewCategory,
    ratingSelected,
    currentView,
    feedbackReviewData,
    setSelectedReviewCategory,
  } = qrFeedbackContextData;

  useEffect(() => {
    if (!isNaN(ratingSelected) && !isEmpty(feedbackReviewData.categories)) {
      setSelectedReviewCategory({
        ...selectedReviewCategory,
        rating: ratingSelected,
      });
    }
  }, [ratingSelected]);

  useEffect(() => {
    if (!isEmpty(feedbackReviewData.categories)) {
      if (reviewComment !== undefined) {
        setSelectedReviewCategory({
          ...selectedReviewCategory,
          comment: reviewComment,
        });
      }
    }
  }, [reviewComment]);

  const handleReviewCommentData = (e) => {
    setCount(e.target.value.length);
    setReviewComment(e.target.value);
  };

  const defaultRatingValue = (cat: typeof category) => {
    if (isInitPreview) return 3;
    if (cat?.rating !== undefined && cat?.rating !== null) return cat.rating;
  };

  const displayName = useMemo(() => {
    const fromData = find(data.categories, { id: category?.id })?.name;
    const resolved = fromData ?? category?.name ?? data.categories?.[0]?.name;
    return typeof resolved === "string" ? resolved.trim() : "";
  }, [data.categories, category?.id, category?.name]);

  return (
    <>
      {currentView !== FEEDBACK_PREVIEW_INDEX.HOME_SINGLE_CATEGORY && !isInitPreview && (
        <FeedbackNavigation hasMultipleCategories={hasMultipleCategories} />
      )}

      <div
        className={`feedback-template__single ${hasMultipleCategories ? "feedback-template__single--multiple-categories" : ""}`}
      >
        {(currentView === FEEDBACK_PREVIEW_INDEX.HOME_SINGLE_CATEGORY || isInitPreview) && (
          <FeedbackHeader data={data} />
        )}
        <div className="feedback-template__categories">
          {(category || isInitPreview) && (
            <div className="feedback-template__category">
              <div className="feedback-form">
                {displayName.length > 0 && (
                  <Typography
                    Tag="p"
                    size="m"
                    classname="feedback-template__category-name"
                    style={{ color: themeColor?.buttonColor }}
                  >
                    {displayName}
                  </Typography>
                )}
                {(isInitPreview || category?.rating !== undefined) && (
                  <div className="feedback-template__category-rating">
                    <StarsRating defaultCheckedValue={defaultRatingValue(category)} />
                  </div>
                )}

                <div className="feedback-template__category-input">
                  <label
                    htmlFor={`feedback-input`}
                    className="feedback-template__label"
                  >
                    {t("textarea_label")}
                  </label>
                  <textarea
                    id={`feedback-input`}
                    className="feedback-template__textarea"
                    name="feedback"
                    maxLength={MAX_CHARACTERS}
                    onChange={(e) => handleReviewCommentData(e)}
                    placeholder={t("textarea_placeholder")}
                    defaultValue={category?.comment}
                  />
                  {/* TODO:: Extend Input component to add maxLength functionality */}
                  <div className="feedback-template__textarea-counter">
                    {count}/{MAX_CHARACTERS}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
